// Shared marks-consistency utilities used by AssessmentTab.
// These are pure helpers (validation + highlighting); side-effects like the
// auto-correction PUT live in autoCorrectResults below.

import React from 'react';

export const THINKING_PHRASES = [
  'Wait,', 'Wait ', 'Re-evaluating', 'Actually,', 'Actually ', 'However, looking at',
  'Note: As per strict', 'Corrected Marks', 'Revised Total', 'Adjusted to',
  'Let me reconsider', 'On second thought', 'I need to reconsider', 'Re-examining',
  'Upon reflection', 're-evaluating strictly'
];

// A fullwidth or Arabic comma is still the phrase "Actually,". Folding
// keeps the same indexes, so the replacement lands on the original line.
function foldThinking(text) {
  return String(text || '')
    .replace(/[\u00a0\u202f]/g, ' ')
    .replace(/[\uff0c\u201a\u060c\u3001]/g, ',')
    .replace(/[\u2018\u2019\u201b]/g, "'");
}

function foldedIndex(line, phrase) {
  const folded = foldThinking(line);
  const needle = foldThinking(phrase);
  const exact = folded.indexOf(needle);
  if (exact >= 0) return exact;
  const lowerLine = folded.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  if (lowerLine.length !== folded.length || lowerNeedle.length !== needle.length) return -1;
  return lowerLine.indexOf(lowerNeedle);
}

function earliestThinkingPhrase(line) {
  let at = -1;
  for (const phrase of THINKING_PHRASES) {
    const index = foldedIndex(line, phrase);
    if (index >= 0 && (at < 0 || index < at)) at = index;
  }
  return at;
}

function thinkingCellEnd(line, from) {
  if (!String(line).trim().startsWith('|')) return line.length;
  for (let i = from; i < line.length; i++) {
    if (line[i] === '|' && line[i - 1] !== '\\') return i;
  }
  return line.length;
}

// Same rule as Backend_Subjective_Checker/utils/thinkingText.js.
// The aside starts at the thinking phrase and runs to the end of that
// table cell, or to the end of the line under the table. The deduction
// written before the phrase stays.
export function thinkingPhrasesIn(text) {
  const raw = String(text || '');
  const found = [];
  for (const phrase of THINKING_PHRASES) {
    if (foldedIndex(raw, phrase) >= 0 && !found.includes(phrase)) found.push(phrase);
  }
  return found;
}

// Every thinking phrase in the text. The mark is the phrase itself
// ("Wait,", "Actually,"), not the sentence that follows it.
function phraseSpansInLine(line) {
  const folded = foldThinking(line);
  const lower = folded.toLowerCase();
  const lowerOk = lower.length === folded.length;
  const spans = [];
  for (const phrase of THINKING_PHRASES) {
    const needle = foldThinking(phrase);
    const lowerNeedle = needle.toLowerCase();
    const useLower = lowerOk && lowerNeedle.length === needle.length && lowerNeedle !== needle;
    let from = 0;
    while (from < folded.length) {
      const exact = folded.indexOf(needle, from);
      const loose = useLower ? lower.indexOf(lowerNeedle, from) : -1;
      let at = exact;
      if (loose >= 0 && (at < 0 || loose < at)) at = loose;
      if (at < 0) break;
      spans.push({ start: at, end: at + needle.length });
      from = at + Math.max(needle.length, 1);
    }
  }
  spans.sort((a, b) => a.start - b.start || b.end - a.end);
  const kept = [];
  let covered = -1;
  for (const span of spans) {
    if (span.start < covered) continue;
    kept.push(span);
    covered = span.end;
  }
  return kept;
}

export function asidePhraseRanges(text) {
  const raw = String(text || '');
  const lines = raw.split('\n');
  const ranges = [];
  let offset = 0;
  for (const line of lines) {
    for (const span of phraseSpansInLine(line)) {
      ranges.push({ start: offset + span.start, end: offset + span.end });
    }
    offset += line.length + 1;
  }
  return ranges;
}

export function firstAsidePhraseRange(text) {
  const ranges = asidePhraseRanges(text);
  return ranges.length ? ranges[0] : null;
}

// Swap each highlighted thinking phrase for "...". The sentence after it stays.
export function replaceAsidePhrases(text) {
  const raw = String(text || '');
  const ranges = asidePhraseRanges(raw);
  if (!ranges.length) return raw;
  let out = '';
  let cursor = 0;
  for (const range of ranges) {
    out += raw.slice(cursor, range.start) + '...';
    cursor = range.end;
  }
  return out + raw.slice(cursor);
}

// The first aside in a table or comment: from the thinking phrase to the
// end of that cell. Indexes match the original text, so an editor can
// select the same span.
export function firstAsideRange(text) {
  const raw = String(text || '');
  const lines = raw.split('\n');
  let offset = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const at = earliestThinkingPhrase(line);
    if (at >= 0) {
      return { start: offset + at, end: offset + thinkingCellEnd(line, at) };
    }
    offset += line.length + 1;
  }
  return null;
}

export function acceptThinkingText(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line) => {
    let out = line;
    for (let guard = 0; guard < 20; guard++) {
      const at = earliestThinkingPhrase(out);
      if (at < 0) break;
      const end = thinkingCellEnd(out, at);
      let after = out.slice(end);
      if (after.startsWith('|')) after = ` ${after}`;
      out = `${out.slice(0, at)}ok${after}`;
    }
    return out;
  }).join('\n');
}

// Pull the roll code out of "(A01)Saher" → "A01"
export const extractRollNumber = (studentName) => {
  if (!studentName) return null;
  const match = studentName.match(/\(([^)]+)\)/);
  return match ? match[1] : null;
};

// Detect duplicate "Total marks:" headers (AI thinking-out-loud signal).
export const hasDuplicateHeaders = (comments) => {
  if (!comments) return false;
  const matches = String(comments).match(/total\s+marks\s*:/gi);
  return !!(matches && matches.length > 1);
};

// A mark is a question score or a paper total, never a calculation step.
//   Q1.i: 3/2    Q.4.a – 6/5    Q2 → 2/1    Total marks: 70/60
// "Calculation error(page2): 114/6" is working, so it is ignored.
const MARK_SEPARATOR = '(?:→|:|>|\u2013|\u2014|\u2212|-)';
const QUESTION_MARK_RE = new RegExp(
  '\\b(Q\\.?[a-z0-9]+(?:\\.[a-z0-9]+)*(?:\\s*\\(\\s*extra\\s*\\))?\\s*' +
    MARK_SEPARATOR + '\\s*)(\\d+(?:\\.\\d+)?)\\s*\\/\\s*(\\d+(?:\\.\\d+)?)',
  'gi'
);
const TOTAL_MARK_RE = new RegExp(
  '((?:total\\s+marks|grand\\s+total|مجموعی\\s+نمبر)\\s*' +
    MARK_SEPARATOR + '?\\s*)(\\d+(?:\\.\\d+)?)\\s*\\/\\s*(\\d+(?:\\.\\d+)?)',
  'gi'
);

// Ranges of "obtained/max" that belong to a question or the paper total
// and have obtained > max. The slice is the fraction only.
export const overMaxMarkRanges = (text) => {
  const src = String(text || '');
  const ranges = [];
  for (const rx of [QUESTION_MARK_RE, TOTAL_MARK_RE]) {
    rx.lastIndex = 0;
    let m;
    while ((m = rx.exec(src)) !== null) {
      const obtained = parseFloat(m[2]);
      const total = parseFloat(m[3]);
      if (!isNaN(obtained) && !isNaN(total) && total > 0 && obtained > total) {
        ranges.push({
          start: m.index + m[1].length,
          end: m.index + m[0].length,
          oStr: m[2],
          tStr: m[3]
        });
      }
    }
  }
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const kept = [];
  for (const r of ranges) {
    const prev = kept[kept.length - 1];
    if (prev && r.start < prev.end) continue;
    kept.push(r);
  }
  return kept;
};

// True when a question score or the paper total has obtained > max.
export const hasMarksExceeded = (marks, comments) => {
  const text = String(comments || '') + ' ' + String(marks || '');
  return overMaxMarkRanges(text).length > 0;
};

// 3-way check: marks ↔ header total ↔ sum of sub-scores. Returns details
// suitable for both the auto-correct decision and the badge UI.
export const validateConsistency = (marks, comments) => {
  const result = {
    isConsistent: true, statedTotal: null, calculatedTotal: null,
    headerStatedTotal: null,
    difference: 0, hasThinkingText: false, thinkingPhrase: '', details: '', subScores: []
  };
  if (!comments || !marks) return result;

  const statedTotal = parseFloat(marks);
  if (isNaN(statedTotal)) return result;
  result.statedTotal = statedTotal;

  // "Total marks: 23.1/25"
  const headerTotalMatch = comments.match(/[Tt]otal\s+marks?[:\s]+([\d.]+)\s*\/\s*[\d.]+/i);
  if (headerTotalMatch) {
    result.headerStatedTotal = Math.round(parseFloat(headerTotalMatch[1]) * 100) / 100;
  }

  // Header sub-scores ONLY: extract the first parenthesized block that
  // contains a Q-pattern, e.g. "(Q1→ 4.6/5 | Q2→ 6/10 | Q3→ 3/5)". The
  // detail breakdown later in the comment ("Q1: 4.6/5 - Spelling mistake...",
  // "Q2.i: 2/2", etc.) is intentionally ignored so we don't double-count.
  // Sub-parts can be Roman numerals or letters, possibly nested (Q1.a.i),
  // and use → / > / : as the separator (Urdu output prefers ":").
  const qScorePattern = /\bQ\.?[a-z0-9]+(?:\.[a-z0-9]+)*\s*[→>:]\s*([\d.]+)\s*\/\s*[\d.]+/gi;
  const subScores = [];
  // Search for the first "(...)" block whose contents contain at least one
  // Q-pattern. That's the header summary; everything after it is detail.
  const bracketRx = /\(([^()]*)\)/g;
  let bracketMatch;
  let headerArea = null;
  while ((bracketMatch = bracketRx.exec(comments)) !== null) {
    if (/\bQ\.?[a-z0-9]/i.test(bracketMatch[1])) {
      headerArea = bracketMatch[1];
      break;
    }
  }
  if (headerArea !== null) {
    let match;
    while ((match = qScorePattern.exec(headerArea)) !== null) {
      const score = parseFloat(match[1]);
      if (!isNaN(score)) subScores.push(score);
    }
  }

  if (subScores.length === 0) return result;

  result.subScores = subScores;
  const calculatedTotal = subScores.reduce((sum, s) => sum + s, 0);
  result.calculatedTotal = Math.round(calculatedTotal * 100) / 100;
  result.difference = Math.round(Math.abs(result.statedTotal - result.calculatedTotal) * 100) / 100;

  // 3-way checks (tolerance 0.1)
  const marksVsSum    = result.difference <= 0.1;
  const headerVsMarks = result.headerStatedTotal === null ||
    Math.round(Math.abs(result.headerStatedTotal - result.statedTotal) * 100) / 100 <= 0.1;
  const headerVsSum   = result.headerStatedTotal === null ||
    Math.round(Math.abs(result.headerStatedTotal - result.calculatedTotal) * 100) / 100 <= 0.1;

  const numbersMatch = marksVsSum && headerVsMarks && headerVsSum;

  for (const phrase of THINKING_PHRASES) {
    if (foldedIndex(comments, phrase) >= 0) {
      result.hasThinkingText = true;
      result.thinkingPhrase = phrase;
      break;
    }
  }

  result.isConsistent = numbersMatch && !result.hasThinkingText;

  if (!numbersMatch) {
    const parts = [];
    if (!headerVsSum && result.headerStatedTotal !== null)
      parts.push(`Header says ${result.headerStatedTotal} but sub-scores sum to ${result.calculatedTotal}`);
    if (!headerVsMarks && result.headerStatedTotal !== null)
      parts.push(`Header says ${result.headerStatedTotal} but marks column says ${result.statedTotal}`);
    if (!marksVsSum)
      parts.push(`${result.subScores.join('+')} = ${result.calculatedTotal}, but marks column says ${result.statedTotal}`);
    let detailMsg = parts.join(' | ');
    if (result.hasThinkingText) detailMsg += ` + thinking text: "${result.thinkingPhrase}"`;
    result.details = detailMsg;
  } else if (result.hasThinkingText) {
    result.details = `Thinking text detected: "${result.thinkingPhrase}"`;
  }

  return result;
};

// Inline highlights: red for over-max sub-scores, amber for duplicate headers.
export const renderCommentsWithOverMaxHighlight = (comments) => {
  const text = String(comments || '');
  if (!text) return 'No comments available';

  const overRanges = overMaxMarkRanges(text).map((r) => ({
    start: r.start,
    end: r.end,
    kind: 'over',
    payload: { oStr: r.oStr, tStr: r.tStr }
  }));

  const dupRanges = [];
  {
    const rx = /(?:total\s+marks|مجموعی\s+نمبر)\s*:\s*\d+(?:\.\d+)?\/\d+(?:\.\d+)?/gi;
    const allHeaders = [];
    let m;
    while ((m = rx.exec(text)) !== null) {
      allHeaders.push({ start: m.index, end: m.index + m[0].length, snippet: m[0] });
    }
    for (let i = 1; i < allHeaders.length; i++) {
      dupRanges.push({ ...allHeaders[i], kind: 'dup' });
    }
  }

  // Drop over-max ranges that fall inside any duplicate-header range so spans
  // don't overlap. Duplicate-header takes visual precedence.
  const filteredOver = overRanges.filter(o =>
    !dupRanges.some(d => o.start >= d.start && o.end <= d.end)
  );

  const ranges = [...filteredOver, ...dupRanges].sort((a, b) => a.start - b.start);

  const out = [];
  let cursor = 0;
  let key = 0;
  for (const r of ranges) {
    if (r.start > cursor) out.push(text.slice(cursor, r.start));
    const slice = text.slice(r.start, r.end);
    if (r.kind === 'over') {
      out.push(
        <span
          key={`over-${key++}`}
          title={`Marks exceed maximum (${r.payload.oStr} > ${r.payload.tStr})`}
          style={{
            background: '#fee2e2', color: '#b91c1c',
            padding: '1px 6px', borderRadius: 4, fontWeight: 700,
            border: '1px solid #fca5a5', display: 'inline-block'
          }}
        >
          {slice}
        </span>
      );
    } else {
      out.push(
        <span
          key={`dup-${key++}`}
          title="Duplicate header — AI repeated the total-marks line"
          style={{
            background: '#fef3c7', color: '#92400e',
            padding: '1px 6px', borderRadius: 4, fontWeight: 700,
            border: '1px solid #fcd34d', display: 'inline-block'
          }}
        >
          {slice}
        </span>
      );
    }
    cursor = r.end;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
};

// Walk results, fix any row whose marks disagree with the sub-score sum, and
// PUT the corrected marks/comments back to the backend. Returns the corrected
// result list (existing rows mutated with `_autoCorrect: { from, details }`).
//
// `apiBaseUrl` and `token` are passed in so this stays decoupled from window
// globals; pass them from the calling component.
export async function autoCorrectResults(results, { apiBaseUrl, token, historyRef }) {
  if (!results || results.length === 0) return results;
  const corrected = [...results];
  let correctionCount = 0;

  for (let i = 0; i < corrected.length; i++) {
    const r = corrected[i];
    const c = validateConsistency(r.marks, r.comments);

    if (!c.isConsistent && c.subScores.length > 0 && c.calculatedTotal !== null) {
      // Always trust the sum of header sub-scores as the correct total.
      let correctTotal = c.calculatedTotal;

      // If the AI self-corrected ("Adjusted to X"), prefer that adjusted value.
      if (c.details && c.details.includes('Self-correction')) {
        const adjMatch = (r.comments || '').match(/[Aa]djusted\s+to\s+([\d.]+)\/[\d.]+/i);
        if (adjMatch) {
          const adjustedVal = parseFloat(adjMatch[1]);
          if (!isNaN(adjustedVal) && c.subScores.length > 0) {
            const adjusted = [...c.subScores];
            adjusted[adjusted.length - 1] = adjustedVal;
            correctTotal = Math.round(adjusted.reduce((s, v) => s + v, 0) * 100) / 100;
          }
        }
      }

      const correctMarks = String(correctTotal);
      // Patch any inline references to the AI's stated total inside the
      // comment so the comment stays consistent with the corrected marks.
      // Three patterns get rewritten:
      //   1. "Total marks: X/Y"           → English header
      //   2. "مجموعی نمبر: X/Y"           → Urdu header
      //   3. The first "X/Y" inside the leading segment (before the first "|"),
      //      where X equals the old stated total. This catches headers like
      //      "(B07)Anas Tanveer: 6.9/20 | …" that don't use the literal
      //      "Total marks" / "مجموعی نمبر" keywords. Restricting the rewrite
      //      to the segment before the first "|" prevents accidentally
      //      touching sub-scores like "Q1: 6.9/10" that appear later.
      let correctedComments = (r.comments || '')
        .replace(/(Total\s+marks?[:\s]+)[\d.]+(\s*\/\s*[\d.]+)/i, `$1${correctMarks}$2`)
        .replace(/(مجموعی\s+نمبر[:\s]+)[\d.]+(\s*\/\s*[\d.]+)/i, `$1${correctMarks}$2`);

      const oldMarksRaw = String(r.marks ?? '').trim();
      if (oldMarksRaw && oldMarksRaw !== correctMarks) {
        const escapedOld = oldMarksRaw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const segments = correctedComments.split('|');
        if (segments.length > 0) {
          // Match "<oldMarks>/<total>" only when not adjacent to other digits,
          // i.e. avoid touching numbers like "16.9/20" if old was "6.9".
          const headerRx = new RegExp(`(^|[^\\d.])${escapedOld}(\\s*\\/\\s*[\\d.]+)`);
          const replaced = segments[0].replace(headerRx, `$1${correctMarks}$2`);
          if (replaced !== segments[0]) {
            segments[0] = replaced;
            correctedComments = segments.join('|');
          }
        }
      }
      const marksChanged    = correctMarks !== r.marks;
      const commentsChanged = correctedComments !== r.comments;

      if (marksChanged || commentsChanged) {
        try {
          const payload = { marks: correctMarks };
          if (commentsChanged) payload.comments = correctedComments;
          await fetch(`${apiBaseUrl}/assess/results/${r.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(payload)
          });
          const acEntry = { from: r.marks, details: c.details };
          if (historyRef && historyRef.current) historyRef.current[r.id] = acEntry;
          corrected[i] = { ...r, marks: correctMarks, comments: correctedComments, _autoCorrect: acEntry };
          correctionCount++;
        } catch (err) {
          console.error(`[AutoCorrect] Failed to update ${r.id}:`, err);
        }
      }
    }
  }

  if (correctionCount > 0) {
    console.log(`[AutoCorrect] Corrected ${correctionCount} result(s)`);
  }
  return corrected;
}
